import type { AxiosError } from 'axios';
import {
  apiClient,
  errorHandler,
  type ErrorResponse,
  type MessageResponse,
} from '../common/api-client';
import type {
  ResultOrError,
  BookingInit,
  ExistingHousehold,
  Article,
  PresignedUrl,
  HouseholdFullData,
  CartConfigurationInput,
  OrderResult,
  LogEntry,
} from '../types/common';
import type { Order } from '../types/order-main';
import type { CartContent } from '../../../types/product-cart';
import type { ArticleWithActionCode } from '../../../types/action-code';

type HouseholdExistsCheckResultResponse = ResultOrError<ExistingHousehold, ErrorResponse>;
type GetCartArticlesResponse = ResultOrError<Article[], ErrorResponse>;
type GetCartContentResponse = ResultOrError<CartContent, ErrorResponse>;
type OrderResponse = ResultOrError<OrderResult, ErrorResponse>;
type LogFrontendResponse = ResultOrError<string, ErrorResponse>;
type DownloadContractResponse = ResultOrError<string, ErrorResponse>;
type GetPresignedUrlResponse = ResultOrError<PresignedUrl, ErrorResponse>;
type GetHouseholdsFullDataResponse = ResultOrError<HouseholdFullData, ErrorResponse>;
type GetActionCodeResponse = ResultOrError<ArticleWithActionCode[], ErrorResponse>;
type BookingInitResponse = ResultOrError<BookingInit, ErrorResponse>;

export async function bookingInit(
  street: string,
  houseNumber: number,
  zipCode: number,
  city: string,
  houseNumberSuffix?: string,
  householdId?: number,
  customerId?: number,
): Promise<BookingInitResponse> {
  try {
    const { data } = await apiClient.get('/booking-init', {
      params: {
        street,
        houseNumber,
        houseNumberSuffix,
        zipCode,
        city,
        householdId,
        customerId,
      },
    });
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}

export async function checkIfHouseholdExists(
  addressId: number,
  firstName: string,
  lastName: string,
  isCompany: boolean,
  dateOfBirth?: string,
  companyRegistrationNumber?: string,
): Promise<HouseholdExistsCheckResultResponse> {
  try {
    const { data } = await apiClient.get('/household-exists', {
      params: {
        addressId,
        firstName,
        lastName,
        isCompany,
        dateOfBirth,
        companyRegistrationNumber,
      },
    });
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}

export async function getCartArticles(
  params?: CartConfigurationInput,
): Promise<GetCartArticlesResponse> {
  try {
    const { data } = await apiClient.get('/cart/articles', { params });
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}

export async function getCartContent(config: Article[]): Promise<GetCartContentResponse> {
  try {
    const { data } = await apiClient.post('/cart/content', config);
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}

export async function downloadContract(
  contractTypeId: number,
  addressId: number,
  formData: string,
): Promise<DownloadContractResponse> {
  try {
    const { data } = await apiClient.post('/download-contract', {
      contractTypeId,
      addressId,
      formData,
    });
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}

export async function getPresignedUrl(fileType: string): Promise<GetPresignedUrlResponse> {
  try {
    const { data } = await apiClient.get('/presigned-url', {
      params: { fileType },
    });
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}

export async function logFrontend(entry: LogEntry): Promise<LogFrontendResponse> {
  try {
    const { data } = await apiClient.post('/log-frontend', entry);
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}

export async function order(orderData: Order): Promise<OrderResponse> {
  try {
    const { data } = await apiClient.post('/order', orderData);
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}

export async function getHouseholdsFullData(
  id: number,
  addressId: number,
): Promise<GetHouseholdsFullDataResponse> {
  try {
    const { data } = await apiClient.get('/get-households-full-data', {
      params: {
        id,
        addressId,
      },
    });
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}

export async function getActionCodesArticles(
  actionCode: string,
  micromarketId: string,
): Promise<GetActionCodeResponse> {
  try {
    const { data } = await apiClient.get('/get-action-codes-articles', {
      params: {
        actionCode,
        micromarketId,
      },
    });
    return [null, data];
  } catch (error: unknown) {
    return [errorHandler(error as AxiosError<MessageResponse>), null];
  }
}
